import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, 
  Search, 
  ChevronRight, 
  Smartphone, 
  Clock, 
  Lock, 
  RefreshCcw,
  Inbox,
  Loader2
} from 'lucide-react';
import { api } from "../services/api";

const PRIMARY = "#363C90";
const FILTERS = ["All", "Pending", "Completed", "Cancelled"];

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const checkAuthAndFetch = async () => {
    const token = localStorage.getItem("user_token");
    if (!token) {
      setIsLoggedIn(false);
      setLoading(false);
      return;
    }
    setIsLoggedIn(true);
    fetchOrders();
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const data = await api.get("/orders");
      setOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuthAndFetch();
  }, []);

  const filteredOrders = orders.filter((order: any) => {
    const matchesFilter = activeFilter === "All" || order.status?.toLowerCase() === activeFilter.toLowerCase();
    const matchesSearch = order.device?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         order.id?.toString().includes(searchTerm);
    return matchesFilter && matchesSearch;
  });

  if (isLoggedIn === false) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center p-6 text-center">
        <div className="w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mb-6">
          <Lock size={40} className="text-[#363C90]" />
        </div>
        <h2 className="text-2xl font-black text-slate-900 mb-2">Login to see orders</h2>
        <p className="text-slate-500 max-w-sm mb-8 font-medium">Please sign in to view your order history and track your device status.</p>
        <button 
          onClick={() => window.dispatchEvent(new CustomEvent('openAuthModal'))}
          className="px-10 py-4 bg-[#363C90] text-white rounded-2xl font-black shadow-lg hover:bg-[#2a2f70] transition-all"
        >
          Sign In Now
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-12 px-6">
      <div className="max-w-[1100px] mx-auto">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div>
            <h1 className="text-3xl font-black text-slate-900 leading-tight">My Orders</h1>
            <p className="text-slate-500 font-medium mt-1">Track and manage your device sales</p>
          </div>

          <div className="relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#363C90] transition-colors" size={18} />
            <input 
              type="text" 
              placeholder="Search by ID or Device..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 pr-6 py-3.5 bg-white border border-slate-200 rounded-2xl w-full md:w-[300px] outline-none focus:border-[#363C90]/30 focus:ring-4 focus:ring-[#363C90]/5 transition-all font-bold text-sm"
            />
          </div>
        </div>

        {/* Filter Bar */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 no-scrollbar">
          {FILTERS.map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`px-6 py-2.5 rounded-xl text-sm font-black transition-all whitespace-nowrap border-2 ${
                activeFilter === filter 
                ? "bg-[#363C90] text-white border-[#363C90] shadow-lg shadow-indigo-100" 
                : "bg-white text-slate-500 border-transparent hover:border-slate-200"
              }`}
            >
              {filter}
            </button>
          ))}
          <button onClick={fetchOrders} className="ml-auto p-2.5 text-slate-400 hover:text-[#363C90] transition-colors">
            <RefreshCcw size={20} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        {/* Orders Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#363C90] mb-4" size={40} />
            <p className="text-slate-400 font-bold">Loading your history...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="bg-white rounded-[40px] p-20 text-center border border-dashed border-slate-200">
            <Inbox size={60} className="text-slate-200 mx-auto mb-6" />
            <h3 className="text-xl font-black text-slate-900 mb-2">No {activeFilter !== "All" ? activeFilter : ""} orders found</h3>
            <p className="text-slate-500 font-medium mb-8 italic">"Great things start with a single sale!"</p>
            <button onClick={() => navigate('/')} className="text-[#363C90] font-black text-sm underline underline-offset-4">Start Selling</button>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredOrders.map((order) => (
              <div 
                key={order.id}
                onClick={() => navigate(`/orders/${order.id}`)}
                className="bg-white border border-slate-100 rounded-[28px] p-6 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:shadow-xl hover:shadow-indigo-50/50 hover:-translate-y-1 transition-all cursor-pointer group"
              >
                <div className="flex items-center gap-5">
                  <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center text-[#363C90] group-hover:bg-[#363C90] group-hover:text-white transition-colors">
                    <Smartphone size={28} />
                  </div>
                  <div>
                    <h4 className="text-lg font-black text-slate-900">{order.device}</h4>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-xs font-black text-[#363C90] bg-indigo-50 px-2 py-0.5 rounded-md">#{order.id}</span>
                      <span className="text-slate-400 text-xs font-bold flex items-center gap-1">
                        <Clock size={12} /> {order.date}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between md:justify-end gap-10 border-t md:border-t-0 pt-4 md:pt-0">
                  <div className="text-right">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Selling Price</p>
                    <p className="text-xl font-black text-slate-900">₹{order.price}</p>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex flex-col items-end">
                      <div className="flex items-center gap-2 px-3 py-1 rounded-full border border-slate-100 bg-slate-50">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: order.color || '#64748B' }} />
                        <span className="text-[10px] font-black uppercase tracking-wider" style={{ color: order.color || '#64748B' }}>
                          {order.status}
                        </span>
                      </div>
                    </div>
                    <div className="w-10 h-10 rounded-full border border-slate-100 flex items-center justify-center text-slate-300 group-hover:bg-[#363C90] group-hover:text-white transition-all">
                      <ChevronRight size={20} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;